import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { SlidersHorizontal, Globe, Search, ShoppingBag, UserPlus, Loader2, AlertTriangle, KeyRound, Database, MailWarning, TrendingUp, FileText, Palette } from "lucide-react";
import aapi, { formatApiError } from "./aapi";
import { THEMES, applyTheme } from "@/theme";
import { PageHeader, inputCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

const Toggle = ({ on, onChange, testid }) => (
  <button onClick={() => onChange(!on)} data-testid={testid} aria-label="Toggle"
    className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-300 ${on ? "bg-gold" : "bg-white/10"}`}>
    <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all duration-300 ${on ? "left-6" : "left-1"}`} />
  </button>
);

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    aapi.get("/settings").then(({ data }) => setForm(data)).catch(() => {});
    loadCounter();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [em, setEm] = useState({ new_email: "", current_password: "" });
  const [emBusy, setEmBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [counterInfo, setCounterInfo] = useState(null);
  const [counterInput, setCounterInput] = useState("");
  const [counterBusy, setCounterBusy] = useState(false);

  const loadCounter = () =>
    aapi.get("/counter").then(({ data }) => {
      setCounterInfo(data);
      setCounterInput(String(data.value));
    }).catch(() => {});

  const setCounterValue = async () => {
    setCounterBusy(true);
    try {
      const { data } = await aapi.put("/counter", { value: parseInt(counterInput, 10) || 0 });
      toast.success(`Counter set to ${data.value.toLocaleString("en-IN")} — audit logged`);
      loadCounter();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCounterBusy(false);
    }
  };

  const resetCounter = async () => {
    setCounterBusy(true);
    try {
      const { data } = await aapi.post("/counter/reset");
      toast.success(`Counter reset to base ${data.value.toLocaleString("en-IN")} — audit logged`);
      loadCounter();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCounterBusy(false);
    }
  };

  const runLegacyImport = async () => {
    setImportBusy(true);
    setImportResult(null);
    try {
      const { data } = await aapi.post("/import/legacy");
      setImportResult(data);
      toast.success(`Import complete — ${data.inserted} users added, ${data.total_users} total`, {
        description: "Silent import — no emails or notifications were sent.",
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImportBusy(false);
    }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setEmBusy(true);
    try {
      const { data } = await aapi.put("/account/email", { new_email: em.new_email.trim(), current_password: em.current_password });
      toast.success(`Admin email updated to ${data.email}`, { description: "Use the new email on your next login." });
      setEm({ new_email: "", current_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEmBusy(false);
    }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) return toast.error("New passwords do not match");
    setPwBusy(true);
    try {
      await aapi.post("/password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Admin password updated", { description: "Use the new password on your next login." });
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPwBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await aapi.put("/settings", form);
      toast.success("Settings saved — in effect immediately");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!form)
    return (
      <div className="flex justify-center py-32">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
      </div>
    );

  return (
    <div data-testid="admin-settings-page">
      <PageHeader title="Settings" sub="Platform-wide switches and identity. Every change is audit-logged." />

      <div className="grid gap-6 lg:grid-cols-2">
        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.04 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8 lg:col-span-2" data-testid="settings-theme-card">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <Palette size={17} className="text-gold" /> Website theme
          </h3>
          <p className="mt-2 text-xs text-neutral-500">
            Poori site par apply hota hai — landing page, customer panel aur admin console. Click karte hi instant preview milta hai, SAVE CHANGES par sabke liye live ho jata hai. PDFs aur emails gold hi rehte hain taaki documents consistent rahen.
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {THEMES.map((t) => {
              const active = (form.theme || "obsidian-gold") === t.id;
              return (
                <button key={t.id} type="button" data-testid={`theme-option-${t.id}`}
                  onClick={() => { set("theme", t.id); applyTheme(t.id); }}
                  className={`rounded-2xl border p-4 text-left transition-all duration-300 ${active ? "border-gold bg-gold/[0.07]" : "border-white/10 hover:border-white/25"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-neutral-100">{t.name}</span>
                    {active && <span className="rounded-full bg-gold/15 px-2 py-0.5 font-mono text-[9px] tracking-[0.12em] text-goldlight">ACTIVE</span>}
                  </div>
                  <p className="mt-1 text-[11px] text-neutral-500">{t.desc}</p>
                  <div className="mt-3 flex gap-1.5">
                    {t.swatches.map((c) => <span key={c} className="h-5 w-8 rounded-md border border-white/10" style={{ background: c }} />)}
                  </div>
                </button>
              );
            })}
          </div>
        </motion.section>
        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <Globe size={17} className="text-gold" /> General
          </h3>
          <div className="mt-6 space-y-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Site name</label>
              <input value={form.site_name} onChange={(e) => set("site_name", e.target.value)} className={`${inputCls} mt-2`} data-testid="settings-site-name" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Tagline</label>
              <input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className={`${inputCls} mt-2`} data-testid="settings-tagline" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Support email</label>
              <input value={form.support_email} onChange={(e) => set("support_email", e.target.value)} className={`${inputCls} mt-2`} data-testid="settings-support-email" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">WhatsApp number</label>
              <input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+91 …" className={`${inputCls} mt-2`} data-testid="settings-whatsapp" />
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.08 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <Search size={17} className="text-gold" /> SEO / Meta
          </h3>
          <p className="mt-2 text-xs text-neutral-500">Applied to the homepage the moment you save.</p>
          <div className="mt-6 space-y-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Meta title</label>
              <input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={`${inputCls} mt-2`} data-testid="settings-seo-title" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Meta description</label>
              <textarea rows={3} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className={`${inputCls} mt-2 resize-none`} data-testid="settings-seo-description" />
            </div>
            <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
              <div className="font-mono text-[9px] tracking-[0.25em] text-neutral-600">SEARCH PREVIEW</div>
              <div className="mt-2 text-sm font-medium text-[#8ab4f8]">{form.seo_title}</div>
              <div className="mt-0.5 line-clamp-2 text-xs text-neutral-500">{form.seo_description}</div>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.16 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
              <ShoppingBag size={17} className="text-gold" /> Order settings
            </h3>
            <Toggle on={form.orders_paused} onChange={(v) => set("orders_paused", v)} testid="settings-orders-paused" />
          </div>
          <p className="mt-2 text-xs text-neutral-500">Pause new orders platform-wide. Running orders are untouched.</p>
          {form.orders_paused && (
            <p className="mt-4 flex gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-xs text-amber-300" data-testid="settings-orders-warning">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              Customers will see your notice instead of placing orders.
            </p>
          )}
          <div className="mt-4">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Pause notice shown to customers</label>
            <textarea rows={2} value={form.orders_pause_message} onChange={(e) => set("orders_pause_message", e.target.value)} className={`${inputCls} mt-2 resize-none`} data-testid="settings-pause-message" />
          </div>
          <div className="mt-4">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Refund policy (shown in Add Funds)</label>
            <textarea rows={2} value={form.refund_policy} onChange={(e) => set("refund_policy", e.target.value)} className={`${inputCls} mt-2 resize-none`} data-testid="settings-refund-policy" />
          </div>
          <div className="mt-4">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Default delivery estimate (days)</label>
            <input type="number" min="0" max="365" value={form.default_eta_days ?? 3} onChange={(e) => set("default_eta_days", e.target.value)}
              className={`${inputCls} mt-2`} data-testid="settings-eta-days" />
            <p className="mt-1.5 text-[10px] text-neutral-600">Naye orders ko automatically itne din ki estimated delivery milegi; har order ki ETA order edit se alag bhi set kar sakte ho.</p>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.24 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
              <UserPlus size={17} className="text-gold" /> User registration
            </h3>
            <Toggle on={form.registration_open} onChange={(v) => set("registration_open", v)} testid="settings-registration-open" />
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            When closed, the signup form rejects new accounts with a polite notice. Existing users are unaffected.
          </p>
          <div className={`mt-4 rounded-xl border px-4 py-3 font-mono text-[10px] tracking-[0.2em] ${
            form.registration_open
              ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
              : "border-rose-400/30 bg-rose-400/10 text-rose-300"
          }`} data-testid="settings-registration-status">
            SIGNUPS CURRENTLY {form.registration_open ? "OPEN" : "CLOSED"}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.3 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8" data-testid="email-control-card">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <MailWarning size={17} className="text-gold" /> Customer email control
          </h3>
          <p className="mt-2 text-xs leading-relaxed text-neutral-500">
            Master switch for every automatic customer email — welcome, order updates, payment alerts, ticket
            replies, statements and password resets. While OFF, customers receive nothing automatic; only emails
            you send manually (Users → Email this user) ever go out. Remember to SAVE ALL SETTINGS after toggling.
          </p>
          <div className="mt-4 flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3.5">
            <span className="text-xs text-neutral-300">Automatic customer emails</span>
            <Toggle on={!!form.emails_auto_enabled} onChange={(v) => set("emails_auto_enabled", v)} testid="settings-emails-auto" />
          </div>
          <div className={`mt-4 rounded-xl border px-4 py-3 text-center font-mono text-[10px] tracking-[0.2em] ${
            form.emails_auto_enabled ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-rose-400/30 bg-rose-400/10 text-rose-300"
          }`} data-testid="settings-emails-status">
            AUTO EMAILS CURRENTLY {form.emails_auto_enabled ? "ON" : "OFF — SILENT MODE"}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.31 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8" data-testid="counter-control-card">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <TrendingUp size={17} className="text-gold" /> Headline order counter
          </h3>
          <p className="mt-2 text-xs leading-relaxed text-neutral-500">
            Customer dashboard "Total Orders" is a persisted value that rises on its own by +min–max every minute
            and +1 for every real order placed (BK-… orders included automatically). It never decreases and resumes
            from the saved value after any downtime. The order database is never touched. SAVE ALL SETTINGS applies
            base/range; SET / RESET below apply instantly.
          </p>
          <div className="mt-5 space-y-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Base number</label>
              <input type="number" min="0" value={form.orders_counter_base} onChange={(e) => set("orders_counter_base", e.target.value)} className={`${inputCls} mt-2`} data-testid="counter-base-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Tick min / minute</label>
                <input type="number" min="0" value={form.counter_tick_min} onChange={(e) => set("counter_tick_min", e.target.value)} className={`${inputCls} mt-2`} data-testid="counter-min-input" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Tick max / minute</label>
                <input type="number" min="0" value={form.counter_tick_max} onChange={(e) => set("counter_tick_max", e.target.value)} className={`${inputCls} mt-2`} data-testid="counter-max-input" />
              </div>
            </div>
            <div className="rounded-xl border border-gold/25 bg-gold/[0.05] px-4 py-3 font-mono text-[10px] tracking-[0.12em] text-goldlight" data-testid="counter-preview">
              AUTO: +{form.counter_tick_min}–{form.counter_tick_max} EVERY MINUTE · +1 PER REAL ORDER · NEVER DECREASES
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="counter-manager">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-600">Current display value (persisted in database)</div>
              <div className="mt-1 text-2xl font-bold text-neutral-100" data-testid="counter-current-value">
                {counterInfo ? Number(counterInfo.value).toLocaleString("en-IN") : "…"}
              </div>
              <div className="mt-0.5 font-mono text-[9px] text-neutral-600" data-testid="counter-meta">
                {counterInfo ? `${counterInfo.db_orders.toLocaleString("en-IN")} real orders in database (kept separate)` : ""}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <input type="number" min="0" value={counterInput} onChange={(e) => setCounterInput(e.target.value)} className={inputCls} data-testid="counter-set-input" />
                <button onClick={setCounterValue} disabled={counterBusy} data-testid="counter-set-button"
                  className="shrink-0 rounded-xl border border-gold/40 px-4 py-2 text-xs font-semibold text-goldlight hover:bg-gold hover:text-black disabled:opacity-50">
                  SET VALUE
                </button>
                <button onClick={resetCounter} disabled={counterBusy} data-testid="counter-reset-button"
                  className="shrink-0 rounded-xl border border-white/15 px-4 py-2 text-xs font-semibold text-neutral-400 hover:border-rose-400/40 hover:text-rose-300 disabled:opacity-50">
                  RESET TO BASE
                </button>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.33 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8" data-testid="statement-control-card">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <FileText size={17} className="text-gold" /> Statement controls
          </h3>
          <p className="mt-2 text-xs leading-relaxed text-neutral-500">
            What every generated Account &amp; Transaction Statement contains. SAVE ALL SETTINGS to apply.
          </p>
          <div className="mt-5 space-y-3">
            {[
              ["statement_lock", "Lock issued statements", "A frozen snapshot of the orders & transactions is stored at generation — later edits never change an issued statement.", "settings-statement-lock"],
              ["statement_include_orders", "Include order history", "Show the full order history table in the statement.", "settings-statement-orders"],
              ["statement_include_transactions", "Include transaction ledger", "Show the credit/debit ledger in the statement.", "settings-statement-transactions"],
            ].map(([key, label, hint, tid]) => (
              <div key={key} className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3.5">
                <div>
                  <div className="text-xs font-medium text-neutral-200">{label}</div>
                  <div className="mt-0.5 text-[10px] leading-relaxed text-neutral-600">{hint}</div>
                </div>
                <Toggle on={!!form[key]} onChange={(v) => set(key, v)} testid={tid} />
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.32 }}
          className="rounded-2xl border border-gold/25 bg-panel/80 p-6 sm:p-8" data-testid="admin-security-card">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <KeyRound size={17} className="text-gold" /> Admin password
          </h3>
          <p className="mt-2 text-xs text-neutral-500">
            Change the master admin password. Audit-logged; takes effect on your next login.
          </p>
          <form onSubmit={changePw} className="mt-6 space-y-4">
            <input type="password" required placeholder="Current password" value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} data-testid="admin-pw-current" />
            <input type="password" required minLength={8} placeholder="New password (min. 8 characters)" value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} data-testid="admin-pw-new" />
            <input type="password" required placeholder="Confirm new password" value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={inputCls} data-testid="admin-pw-confirm" />
            <button type="submit" disabled={pwBusy} data-testid="admin-pw-submit"
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-gold/40 bg-gold/10 py-3 text-xs font-bold tracking-[0.15em] text-goldlight transition-colors duration-300 hover:bg-gold/20 disabled:opacity-50">
              {pwBusy && <Loader2 size={14} className="animate-spin" />}
              UPDATE PASSWORD
            </button>
          </form>

          <div className="mt-6 border-t border-white/10 pt-6">
            <h4 className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Admin email</h4>
            <p className="mt-1 text-[11px] text-neutral-600">The address you sign in with. Confirmed with your current password.</p>
            <form onSubmit={changeEmail} className="mt-4 space-y-4">
              <input type="email" required placeholder="New admin email" value={em.new_email}
                onChange={(e) => setEm({ ...em, new_email: e.target.value })} className={inputCls} data-testid="admin-email-new" />
              <input type="password" required placeholder="Confirm with current password" value={em.current_password}
                onChange={(e) => setEm({ ...em, current_password: e.target.value })} className={inputCls} data-testid="admin-email-password" />
              <button type="submit" disabled={emBusy} data-testid="admin-email-submit"
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-gold/40 bg-gold/10 py-3 text-xs font-bold tracking-[0.15em] text-goldlight transition-colors duration-300 hover:bg-gold/20 disabled:opacity-50">
                {emBusy && <Loader2 size={14} className="animate-spin" />}
                UPDATE EMAIL
              </button>
            </form>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.36 }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8" data-testid="legacy-import-card">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <Database size={17} className="text-gold" /> Legacy user import
          </h3>
          <p className="mt-2 text-xs leading-relaxed text-neutral-500">
            One-time silent import of the old panel's customers (3,609 accounts) with their wallet balances and
            carry-forward funds entries. Safe to re-run — existing accounts are skipped, and no emails or
            notifications are ever sent to anyone.
          </p>
          <button onClick={runLegacyImport} disabled={importBusy} data-testid="legacy-import-button"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-xs font-bold tracking-[0.12em] text-black transition-shadow duration-300 hover:shadow-[0_8px_30px_rgb(var(--gold)_/_0.35)] disabled:opacity-50">
            {importBusy && <Loader2 size={14} className="animate-spin" />}
            {importBusy ? "IMPORTING…" : "IMPORT LEGACY USERS NOW"}
          </button>
          {importResult && (
            <div className="mt-4 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-xs leading-relaxed text-emerald-300" data-testid="legacy-import-result">
              Added <strong>{importResult.inserted}</strong> users · carry-forward entries <strong>{importResult.carry_forward}</strong> · total users now <strong>{importResult.total_users}</strong>
            </div>
          )}
        </motion.section>
      </div>

      <button onClick={save} disabled={busy} data-testid="settings-save-button"
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-4 text-sm font-bold tracking-[0.12em] text-black shadow-[0_8px_30px_rgb(var(--gold)_/_0.25)] transition-shadow duration-300 hover:shadow-[0_8px_40px_rgb(var(--gold)_/_0.45)] disabled:opacity-50">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <SlidersHorizontal size={15} />}
        SAVE ALL SETTINGS
      </button>
    </div>
  );
}
